import { ActionIcon, Checkbox, Flex, Popover, Text } from "@mantine/core"
import { ColumnsIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

export type ColumnVisibility = {
  key: string
  label: string
  visible: boolean
  locked?: boolean
}

type Props = {
  columns: ColumnVisibility[]
  onChange: (columns: ColumnVisibility[]) => void
  storageKey?: string
}

export function ColumnToggle({ columns, onChange, storageKey }: Props) {
  const [opened, setOpened] = useState(false)
  const { t } = useTranslation("common")

  useEffect(() => {
    if (storageKey) {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        try {
          const parsed = JSON.parse(saved) as Record<string, boolean>
          const updated = columns.map((col) => ({
            ...col,
            visible: parsed[col.key] !== undefined ? parsed[col.key] : col.visible,
          }))
          onChange(updated)
        } catch {
          // ignore
        }
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (key: string) => {
    const updated = columns.map((col) => (col.key === key && !col.locked ? { ...col, visible: !col.visible } : col))
    onChange(updated)
    if (storageKey) {
      const map: Record<string, boolean> = {}
      updated.forEach((col) => (map[col.key] = col.visible))
      localStorage.setItem(storageKey, JSON.stringify(map))
    }
  }

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" shadow="md" width={240}>
      <Popover.Target>
        <ActionIcon variant="light" color="gray" size="lg" radius="md" onClick={() => setOpened(!opened)}>
          <ColumnsIcon size={18} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown>
        <Text fw={600} fz={14} mb="xs">
          {t("columns.label")}
        </Text>
        <Flex direction="column" gap={6}>
          {columns.map((col) => (
            <Checkbox
              key={col.key}
              label={col.label}
              checked={col.visible}
              onChange={() => toggle(col.key)}
              disabled={col.locked}
              size="sm"
            />
          ))}
        </Flex>
      </Popover.Dropdown>
    </Popover>
  )
}
